import type { AnalyticsProvider, AnalyticsContext } from '../analytics';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('POSTHOG_PROVIDER');

// posthog-js is initialized in App.tsx via posthog.init(); this provider
// wraps the already-initialized global instance so we don't double-init.
export class PostHogProvider implements AnalyticsProvider {
  readonly name = 'posthog';

  async init(_ctx: AnalyticsContext): Promise<void> {
    // PostHog is initialized in App.tsx before analytics.init() is called.
    // Nothing to do here — just confirm it's ready.
    try {
      const ph = await this.getPostHog();
      if (!ph) {
        log.warn('posthog-js not initialized yet');
      }
    } catch (err) {
      log.warn('PostHog not available:', err);
    }
  }

  private async getPostHog() {
    try {
      const { default: posthog } = await import('posthog-js');
      return posthog;
    } catch {
      return null;
    }
  }

  track(event: string, props: Record<string, unknown>): void {
    import('posthog-js').then(({ default: posthog }) => {
      posthog.capture(event, props);
    }).catch((err) => log.warn('PostHog capture failed:', err));
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    import('posthog-js').then(({ default: posthog }) => {
      posthog.identify(userId, traits);
    }).catch((err) => log.warn('PostHog identify failed:', err));
  }

  page(path: string, props?: Record<string, unknown>): void {
    // PostHog captures pageviews automatically; manual capture for SPA route changes
    import('posthog-js').then(({ default: posthog }) => {
      posthog.capture('$pageview', { $current_url: path, ...props });
    }).catch((err) => log.warn('PostHog pageview failed:', err));
  }

  group(orgId: string, traits?: Record<string, unknown>): void {
    import('posthog-js').then(({ default: posthog }) => {
      posthog.group('org', orgId, traits);
    }).catch((err) => log.warn('PostHog group failed:', err));
  }

  alias(anonymousId: string, userId: string): void {
    // Stitch pre-auth anonymous events to the identified user so pre-signup
    // page views and marketing_cta_clicked events appear in the same funnel.
    import('posthog-js').then(({ default: posthog }) => {
      posthog.alias(userId, anonymousId);
    }).catch((err) => log.warn('PostHog alias failed:', err));
  }

  reset(): void {
    import('posthog-js').then(({ default: posthog }) => {
      posthog.reset();
    }).catch((err) => log.warn('PostHog reset failed:', err));
  }

  async flush(): Promise<void> {
    // posthog-js batches automatically; no manual flush API
    return Promise.resolve();
  }
}
